import { CacheableMethod, LoginService } from "@edtell-common";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Observer } from "rxjs";
import { apiURL } from "../../../../environments/environment";
import { PaymentCodeDataInterface } from "../interfaces/payment-code-data.interface";
import { PaymentModuleDataInterface } from "../interfaces/payment-module-data.interface";

@Injectable({ providedIn: "root" })
export class PaymentModuleService {
  credentialStatus = false;
  private httpOptions: object;
  private textHttpOptions: object;
  transactionState: Observable<any>;

  private transactionObserver: Observer<any>;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
  ) {
    this.httpOptions = loginService.httpOptions;
    this.textHttpOptions = loginService.textHttpOptions;

    this.transactionState = new Observable((o) => {
      this.transactionObserver = o;
    });
  }

  createPaymentModule(paymentModule: PaymentModuleDataInterface) {
    return this.http.post(
      `${apiURL}/customer-app/payment-module`,
      paymentModule,
      this.httpOptions,
    );
  }

  getPaymentModuleById(id: number) {
    return this.http.get<PaymentModuleDataInterface>(
      `${apiURL}/customer-app/payment-module?id=${id}`,
      this.httpOptions,
    );
  }

  updatePaymentModule(paymentModule: PaymentModuleDataInterface) {
    return this.http.put(
      `${apiURL}/customer-app/payment-module`,
      paymentModule,
      this.httpOptions,
    );
  }

  deletePaymentModuleById(id: number) {
    return this.http.delete(
      `${apiURL}/customer-app/payment-module?id=${id}`,
      this.httpOptions,
    );
  }

  getPaymentModulesByCustomerId(customerId: number) {
    return this.http.get<PaymentModuleDataInterface[]>(
      `${apiURL}/customer-app/payment-module/all?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getCustomerPaymentModules(customerId: number) {
    return this.http.get<PaymentModuleDataInterface[]>(
      `${apiURL}/customer-app/payment-module/customer-payment-modules?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  checkDefaultPaymentModule(customerId: number) {
    return this.http.get<boolean>(
      `${apiURL}/customer-app/payment-module/has-default?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  checkRegId(regId: number) {
    return this.http.get<boolean>(
      `${apiURL}/registrations-app/registrations/profile/reg-id-check?regId=${regId}`,
      this.httpOptions,
    );
  }

  getEducereDefaultPaymentModule() {
    return this.http.get<PaymentModuleDataInterface>(
      `${apiURL}/customer-app/payment-module/educere-default`,
      this.httpOptions,
    );
  }

  checkCredentials(pmId: number) {
    return this.http.get(
      `${apiURL}/authorize-net-app/authorize-net/check-credentials?pmId=${pmId}`,
      this.textHttpOptions,
    );
  }

  getBatchList(pmId: number, startTime: string, endTime: string) {
    return this.http.get(
      `${apiURL}/authorize-net-app/authorize-net/batch?pmId=${pmId}&startTime=${startTime}&endTime=${endTime}`,
      this.textHttpOptions,
    );
  }

  getSettledTransactionList(pmId: number, batchId: number) {
    return this.http.get(
      `${apiURL}/authorize-net-app/authorize-net/settled?pmId=${pmId}&batchId=${batchId}`,
      this.textHttpOptions,
    );
  }

  getUnsettledTransactionList(pmId: number) {
    return this.http.get(
      `${apiURL}/authorize-net-app/authorize-net/unsettled?pmId=${pmId}`,
      this.textHttpOptions,
    );
  }

  getTransactionInfo(pmId: number, transId: number) {
    return this.http.get(
      `${apiURL}/authorize-net-app/authorize-net/transaction?pmId=${pmId}&transactionId=${transId}`,
      this.textHttpOptions,
    );
  }

  updateHeldTransaction(pmId: number, transId: number, action) {
    return this.http.put(
      `${apiURL}/authorize-net-app/authorize-net/update-transaction?pmId=${pmId}&transactionId=${transId}&action=${action}`,
      {},
      this.textHttpOptions,
    );
  }

  setKeyById(newKey: string, pmId: number) {
    return this.http.put(
      `${apiURL}/customer-app/payment-module/key?key=${newKey}&id=${pmId}`,
      {},
      this.textHttpOptions,
    );
  }

  // ############################# PAYMENT CODE API #############################

  getPaymentCodeById(id: number) {
    return this.http.get<PaymentCodeDataInterface>(
      `${apiURL}/customer-app/payment-module/payment-code?paymentCodeId=${id}`,
      this.httpOptions,
    );
  }

  updateAllPaymentCodes(paymentCodes: PaymentCodeDataInterface[]) {
    return this.http.put<PaymentCodeDataInterface[]>(
      `${apiURL}/customer-app/payment-module/payment-code/all`,
      paymentCodes,
      this.httpOptions,
    );
  }

  getAllPaymentCodesWithCustomerName() {
    return this.http.get<PaymentCodeDataInterface[]>(
      `${apiURL}/customer-app/payment-module/payment-code/customer-name`,
      this.httpOptions,
    );
  }

  getAllPaymentCodesForCustomer(customerId: number) {
    return this.http.get<PaymentCodeDataInterface[]>(
      `${apiURL}/customer-app/payment-module/payment-code/all?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  createPaymentCode(paymentCode: PaymentCodeDataInterface) {
    return this.http.post<PaymentCodeDataInterface>(
      `${apiURL}/customer-app/payment-module/payment-code`,
      paymentCode,
      this.httpOptions,
    );
  }

  updatePaymentCode(paymentCode: PaymentCodeDataInterface) {
    return this.http.put<PaymentCodeDataInterface>(
      `${apiURL}/customer-app/payment-module/payment-code`,
      paymentCode,
      this.httpOptions,
    );
  }

  deletePaymentCodeById(id: number) {
    return this.http.delete<boolean>(
      `${apiURL}/customer-app/payment-module/payment-code?id=${id}`,
      this.httpOptions,
    );
  }

  isPaymentCodeUnique(paymentCode: string, customerId: number) {
    return this.http.get<boolean>(
      `${apiURL}/customer-app/payment-module/payment-code/isUnique?paymentCode=${paymentCode}&customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getActivePaymentCodesByCustomerId(customerId: number) {
    return this.http.get<PaymentCodeDataInterface[]>(
      `${apiURL}/customer-app/payment-module/payment-code/active?customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getPaymentCodeByPaymentCodeAndCustomerId(
    paymentCode: string,
    customerId: number,
  ) {
    return this.http.get<PaymentCodeDataInterface>(
      `${apiURL}/customer-app/payment-module/payment-code/code?paymentCode=${paymentCode}&customerId=${customerId}`,
      this.httpOptions,
    );
  }

  getActivePaymentForLicenseEditing(customerId: number, licenseId: number) {
    return this.http.get<PaymentCodeDataInterface[]>(
      `${apiURL}/customer-app/payment-module/payment-code/license-edit?customerId=${customerId}&licenseId=${licenseId}`,
      this.httpOptions,
    );
  }
}
